/*!
 * 稽核紀錄（v2.32.0）
 *
 * 只有 `manage_users`（Brad + Apex）看得到。
 *
 * 記兩類事：
 *   1. 帳號生命週期 —— 登入成功/失敗、登出、改密碼、建立/刪除帳號、改權限
 *   2. 敏感資料讀取 —— 誰在什麼時候看了哪一週的營收（VIEW_REVENUE）
 *      以及誰被擋下來過（ACCESS_DENIED）
 *
 * ⚠️ VIEW_REVENUE 在後端有 60 秒節流（同一人 / 同一路徑 / 同一週期只記一筆），
 * 因為營收頁一次載入會打十幾支 API。所以這裡看到的筆數是「瀏覽次數」的
 * 近似值，不是 HTTP 請求數 —— 要的是「誰看過」不是「打了幾次」。
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Button, Card, CardBody, CardHeader, CardTitle, Col,
  FormGroup, Input, Label, Row, Table,
} from "reactstrap";
import axios from "axios";

import {
  COLORS, inputStyle, labelStyle, saveBtnStyle, softBadge, tdStyle, thStyle,
} from "constant/adminTheme";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyLoading, { TinyLoadingOverlay } from "components/tiny/TinyLoading";
/** 軟色調徽章。⚠️ 不要改回 reactstrap 的 <Badge color="...">，見 adminTheme.js 說明。 */
const Pill = ({ color, children, style }) => (
  <span style={softBadge(color, style)}>{children}</span>
);

const PAGE_SIZE = 50;

// 動作的顯示名稱與配色。找不到就原樣顯示 —— 後端加了新動作而前端還沒跟上時，
// 寧可顯示英文代號，也不要靜默漏掉一整類事件。
const ACTION_META = {
  LOGIN_OK: { label: "登入成功", color: COLORS.success, icon: "fa-sign-in" },
  LOGIN_FAIL: { label: "登入失敗", color: COLORS.warning, icon: "fa-exclamation-triangle" },
  LOGOUT: { label: "登出", color: COLORS.textMuted, icon: "fa-sign-out" },
  PASSWORD_CHANGE: { label: "修改密碼", color: COLORS.info, icon: "fa-key" },
  PASSWORD_RESET: { label: "重設密碼", color: COLORS.warning, icon: "fa-refresh" },
  USER_CREATE: { label: "建立帳號", color: COLORS.accent, icon: "fa-user-plus" },
  USER_DELETE: { label: "刪除帳號", color: COLORS.danger, icon: "fa-user-times" },
  USER_CAPS_CHANGE: { label: "變更權限", color: COLORS.accent, icon: "fa-sliders" },
  USER_RENAME: { label: "改顯示名稱", color: COLORS.textMuted, icon: "fa-pencil" },
  LEGACY_DISABLED: { label: "停用共用帳號", color: COLORS.danger, icon: "fa-power-off" },
  VIEW_REVENUE: { label: "檢視營收", color: COLORS.info, icon: "fa-line-chart" },
  ACCESS_DENIED: { label: "存取被拒", color: COLORS.danger, icon: "fa-ban" },
  OVB_RAKEBACK_RUN: { label: "OVB 退水計算", color: COLORS.accent, icon: "fa-calculator" },
  OVB_RAKEBACK_PAID: { label: "OVB 退水發放", color: COLORS.success, icon: "fa-square-check" },
  OVB_RAKEBACK_ADJUST: { label: "OVB 補發／調整", color: COLORS.warning, icon: "fa-square-plus" },
  OVB_RAKEBACK_TIERS: { label: "OVB 級距變更", color: COLORS.warning, icon: "fa-sliders" },
  CASHFLOW_DIFF_ACK: { label: "金流差異確認", color: COLORS.success, icon: "fa-clipboard-check" },
  CASHFLOW_DIFF_ACK_REVOKE: { label: "撤銷差異確認", color: COLORS.warning, icon: "fa-rotate-left" },
  PARTNER_PORTAL_OPEN: { label: "開啟合作夥伴專區", color: COLORS.info, icon: "fa-handshake" },
};

const fmtMoney = (v) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString("zh-TW", { hour12: false });
};

const renderDetail = (row) => {
  const d = row.detail || {};
  if (row.action === "VIEW_REVENUE") return d.week ? `週期 ${d.week}` : "";
  if (row.action === "ACCESS_DENIED") return d.required_cap ? `缺少 ${d.required_cap}` : "";
  if (row.action === "LOGIN_FAIL") {
    return { invalid_credentials: "帳號或密碼錯誤", account_disabled: "帳號已停用", account_locked: "帳號鎖定中" }[d.reason] || d.reason || "";
  }
  if (row.action === "USER_CAPS_CHANGE" && d.before && d.after) {
    const diff = Object.keys(d.after)
      .filter((k) => d.before[k] !== d.after[k])
      .map((k) => `${k}: ${d.before[k] ? "有" : "無"} → ${d.after[k] ? "有" : "無"}`);
    return diff.join("， ") || "無變更";
  }
  if (row.action === "USER_CREATE" && d.caps) {
    const on = Object.keys(d.caps).filter((k) => d.caps[k]);
    return on.length ? `權限：${on.join("、")}` : "無額外權限";
  }
  if (row.action === "LEGACY_DISABLED") return (d.disabled || []).join("、");
  if (row.action === "OVB_RAKEBACK_RUN") {
    return `合計 ${fmtMoney(d.total)}（玩家 ${d.players ?? 0} 列、代理 ${d.agents ?? 0} 列）`;
  }
  if (row.action === "OVB_RAKEBACK_PAID") {
    const rows = d.rows || [];
    return `${d.paid ? "勾選已發放" : "取消已發放"} ${rows.length} 列：${rows.map((r) => r.key).join("、")}`;
  }
  if (row.action === "OVB_RAKEBACK_ADJUST") {
    return `${d.op === "delete" ? "刪除" : "新增"} ${d.member_id} ${fmtMoney(d.amount)}（${d.reason || ""}）`;
  }
  if (row.action === "OVB_RAKEBACK_TIERS") {
    const t = (d.tiers || []).map((x) => `≥${x.min_rake} ${x.rate_pct}%`).join("、");
    return `${t}${d.note ? `（${d.note}）` : ""}`;
  }
  if (row.action === "CASHFLOW_DIFF_ACK" || row.action === "CASHFLOW_DIFF_ACK_REVOKE") {
    // target = "<settlement|commission>:<source_id>:<期間>"
    const [kind, , period] = (row.target || "").split(":");
    const what = `${period || ""} ${kind === "commission" ? "佣金" : "實際交收"}`;
    const diff = `差 ${d.diff > 0 ? "+" : ""}${fmtMoney(d.diff)}`;
    return `${what}　${diff}${d.reason ? `（${d.reason}）` : ""}`;
  }
  const keys = Object.keys(d);
  return keys.length ? JSON.stringify(d) : "";
};

export default function TinyAuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ actor: "", action: "", date_from: "", date_to: "" });
  const [options, setOptions] = useState({ actors: [], actions: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get("/api/audit-logs/filters")
      .then((r) => { if (r.data.code === 1) setOptions(r.data.data); })
      .catch(() => {});
  }, []);

  const load = useCallback(async (targetPage) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: targetPage, page_size: PAGE_SIZE });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await axios.get(`/api/audit-logs?${params}`);
      if (res.data.code === 1) {
        setLogs(res.data.data.logs || []);
        setTotal(res.data.data.total || 0);
        setPage(targetPage);
      } else {
        setError(res.data.msg || "載入失敗");
      }
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // load 已用 useCallback 綁在 filters 上，filters 一變就會是新的 load，
  // 所以只依賴 load 即可（也避免 filters 變更時漏刷）。
  useEffect(() => { load(1); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="content">
      {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

      {/* ⚠️ 這頁是 platform:"system" 不是 "tiny"，但側欄對 system / common
          是**永遠顯示**的 —— 使用者看到的就是同一批頁面，所以一樣要套公版。 */}
      <TinyPageHeader
        title="稽核紀錄"
        description={`誰在什麼時候做了什麼。保留一年（超過的由資料庫自動清除）。共 ${total.toLocaleString()} 筆。`}
      />

      <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
        <CardBody>
          {/* ---- 篩選 ---- */}
          <Row className="align-items-end mb-3">
            <Col md="3">
              <FormGroup className="mb-0">
                <Label style={labelStyle}>使用者</Label>
                <Input type="select" style={inputStyle} value={filters.actor}
                  onChange={(e) => setFilters({ ...filters, actor: e.target.value })}>
                  <option value="">全部</option>
                  {options.actors.map((a) => <option key={a} value={a}>{a}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup className="mb-0">
                <Label style={labelStyle}>動作</Label>
                <Input type="select" style={inputStyle} value={filters.action}
                  onChange={(e) => setFilters({ ...filters, action: e.target.value })}>
                  <option value="">全部</option>
                  {options.actions.map((a) => (
                    <option key={a} value={a}>{ACTION_META[a]?.label || a}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup className="mb-0">
                <Label style={labelStyle}>起始日</Label>
                <Input type="date" style={inputStyle} value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup className="mb-0">
                <Label style={labelStyle}>結束日</Label>
                <Input type="date" style={inputStyle} value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup className="mb-0">
                {/* 隱形 Label 讓按鈕跟上面的輸入框對齊 */}
                <Label style={{ ...labelStyle, visibility: "hidden" }}>操作</Label>
                <Button block style={saveBtnStyle(false)}
                  onClick={() => setFilters({ actor: "", action: "", date_from: "", date_to: "" })}>
                  清除篩選
                </Button>
              </FormGroup>
            </Col>
          </Row>

          {/* ---- 表格 ---- */}
          {loading && !logs.length ? (
            <TinyLoading variant="block" text="載入中" />
          ) : (
            <TinyLoadingOverlay active={loading} text="載入中">
            <div style={{ overflowX: "auto" }}>
              <Table responsive style={{ color: COLORS.textSecondary }}>
                <thead>
                  <tr>
                    <th style={thStyle}>時間</th>
                    <th style={thStyle}>使用者</th>
                    <th style={thStyle}>動作</th>
                    <th style={thStyle}>對象</th>
                    <th style={thStyle}>細節</th>
                    <th style={thStyle}>來源 IP</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((row, i) => {
                    const meta = ACTION_META[row.action] || { label: row.action, color: COLORS.textMuted, icon: "fa-circle-dot" };
                    return (
                      <tr key={`${row.ts}-${i}`}>
                        <td style={{ ...tdStyle, fontSize: 12, color: COLORS.textMuted }}>{fmtTime(row.ts)}</td>
                        <td style={{ ...tdStyle, fontWeight: 600, color: COLORS.textPrimary }}>{row.actor}</td>
                        <td style={tdStyle}>
                          <Pill color={meta.color}>
                            <i className={`fa ${meta.icon} mr-1`} />{meta.label}
                          </Pill>
                          {row.result === "denied" && (
                            <Pill color={COLORS.danger} style={{ marginLeft: 6 }}>拒絕</Pill>
                          )}
                          {row.result === "failed" && (
                            <Pill color={COLORS.warning} style={{ marginLeft: 6 }}>失敗</Pill>
                          )}
                        </td>
                        <td style={{ ...tdStyle, fontSize: 12, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}
                          title={row.target || ""}>
                          {row.target || "—"}
                        </td>
                        <td style={{ ...tdStyle, fontSize: 12, whiteSpace: "normal" }}>{renderDetail(row) || "—"}</td>
                        <td style={{ ...tdStyle, fontSize: 12, color: COLORS.textDimmed }}>{row.ip || "—"}</td>
                      </tr>
                    );
                  })}
                  {!logs.length && (
                    <tr><td colSpan="6" style={{ ...tdStyle, textAlign: "center", color: COLORS.textMuted }}>
                      沒有符合條件的紀錄
                    </td></tr>
                  )}
                </tbody>
              </Table>
            </div>
            </TinyLoadingOverlay>
          )}

          {/* ---- 分頁 ---- */}
          {totalPages > 1 && (
            <Row className="align-items-center mt-2">
              <Col style={{ color: COLORS.textMuted, fontSize: 13 }}>
                第 {page} / {totalPages} 頁
              </Col>
              <Col className="text-right">
                <Button size="sm" style={cancelable(page > 1)} disabled={page <= 1} onClick={() => load(page - 1)}>
                  <i className="fa fa-chevron-left" /> 上一頁
                </Button>{" "}
                <Button size="sm" style={cancelable(page < totalPages)} disabled={page >= totalPages} onClick={() => load(page + 1)}>
                  下一頁 <i className="fa fa-chevron-right" />
                </Button>
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

const cancelable = (enabled) => ({
  background: enabled ? "rgba(102,126,234,0.2)" : "rgba(149,165,166,0.15)",
  border: `1px solid ${enabled ? COLORS.accent : "rgba(149,165,166,0.4)"}`,
  color: enabled ? COLORS.accent : COLORS.textDimmed,
  borderRadius: 6,
});
